// src/billing/useSubscriptionRead.js — the Piani screen's one read (GET /billing/subscription) and the bounded
// wait after a write the backend only reflects via Stripe webhooks.
//
// ⭐ Sequence ticket: every reload takes a ticket; an answer (or failure) whose ticket is no longer the latest is
// dropped, so a stale read never lands over a fresh one (CLAUDE.md "Async reads carry a sequence ticket").
// ⛔ CUSTOMER_ADMIN_REQUIRED / NO_CUSTOMER hide the screen ("not for you"), never an error.
//
// ⭐ v203 `settle(isSettled)`: /cancel (scheduled), /resume, an immediate /change-plan and an immediate cancel
// reach GET only through Stripe webhooks, seconds later (BE billing_lifecycle.py:341-351, :460-476;
// billing_plan.py:278-284). So after such a write the page re-reads fresh every SETTLE_POLL_MS, at most
// SETTLE_MAX_POLLS times, until `isSettled(data)` holds; `settling` is true meanwhile (the page disables its
// actions). On timeout `lagging` turns true — a quiet note, not an error — until a later read satisfies the same
// predicate. `settle` resolves after its FIRST read, so the caller can open the outcome as the confirm closes.
// ⭐ v204 `settle(isSettled, { quiet, maxPolls })`: a shorter wait that ends WITHOUT the lag note — for the return
// from Stripe's payment tab, where the user may simply not have paid (nothing is propagating, so no "may take a few
// minutes" note).
import { useCallback, useEffect, useRef, useState } from 'react';
import { getSubscription } from './billingApi.js';

export const HIDE_CODES = new Set(['CUSTOMER_ADMIN_REQUIRED', 'NO_CUSTOMER']);
export const SETTLE_POLL_MS = 3000;
export const SETTLE_MAX_POLLS = 10;

const HIDDEN = { data: null, loading: false, loadError: null, hidden: true };

export default function useSubscriptionRead() {
  const [view, setView] = useState({ data: null, loading: true, loadError: null, hidden: false });
  const [settling, setSettling] = useState(false);
  const [lagging, setLagging] = useState(false);
  const mounted = useRef(true);
  const seq = useRef(0);
  const timer = useRef(null);
  const lagCheck = useRef(null);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; clearTimeout(timer.current); };
  }, []);

  /** @returns {Promise<object|null>} the data of THIS read, or null if it failed or went stale */
  const reload = useCallback(async ({ fresh = false } = {}) => {
    seq.current += 1;
    const ticket = seq.current;
    setView((v) => ({ ...v, loading: true }));
    try {
      const d = await getSubscription({ fresh });
      if (!mounted.current || ticket !== seq.current) return null;
      setView({ data: d, loading: false, loadError: null, hidden: false });
      if (lagCheck.current && lagCheck.current(d)) { lagCheck.current = null; setLagging(false); }
      return d;
    } catch (err) {
      if (!mounted.current || ticket !== seq.current) return null;
      if (HIDE_CODES.has(err?.code)) setView(HIDDEN);
      else setView((v) => ({ ...v, loading: false, loadError: err }));
      return null;
    }
  }, []);

  const settle = useCallback(async (isSettled, { quiet = false, maxPolls = SETTLE_MAX_POLLS } = {}) => {
    clearTimeout(timer.current);
    lagCheck.current = null;
    setLagging(false);
    setSettling(true);
    let polls = 0;
    const tick = async () => {
      const d = await reload({ fresh: true });
      if (!mounted.current) return;
      polls += 1;
      if (d && isSettled(d)) { setSettling(false); return; }
      if (polls >= maxPolls) {
        setSettling(false);
        if (quiet) return;
        lagCheck.current = isSettled;
        setLagging(true);
        return;
      }
      timer.current = setTimeout(tick, SETTLE_POLL_MS);
    };
    await tick();
  }, [reload]);

  const hide = useCallback(() => setView(HIDDEN), []);

  return { ...view, settling, lagging, reload, settle, hide };
}
